import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, ConfirmationModal} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import type {BusinessRFI} from '@/types';
import {BusinessRFIStatus} from '@/types';

import RequestReuploadModal from './RequestReuploadModal';
import SendBusinessRFIModal from './SendBusinessRFIModal';
import UploadRFIDocModal from './UploadRFIDocModal';
import {useBusinessRFIActions} from './useBusinessRFIActions';

const BusinessRFIActions = ({
  rfi,
  dataCy,
}: {
  rfi: BusinessRFI;
  dataCy?: string;
}) => {
  const {
    isResolving,
    isRequesting,
    isUploading,
    resolve,
    requestReupload,
    uploadRFIDocs,
  } = useBusinessRFIActions(rfi);

  const [showResolve, setShowResolve] = useState(false);
  const [showReupload, setShowReupload] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showSpecify, setShowSpecify] = useState(false);

  const isResolved = rfi.status === BusinessRFIStatus.Resolved;
  // A draft has no ask yet, so every action that assumes documents were
  // requested is meaningless until one is named.
  const isDraft = rfi.status === BusinessRFIStatus.Draft;

  return (
    <div className="flex flex-wrap gap-3 mt-6">
      <PermissionsProvider permission={AfriexAdminPermission.RFIManage}>
        {isDraft && (
          <Button
            dataCy={dataCy ? `${dataCy}-request-documents` : undefined}
            colorScheme="cyan"
            onClick={() => setShowSpecify(true)}
          >
            Request documents
          </Button>
        )}
        <Button
          dataCy={dataCy ? `${dataCy}-upload-documents` : undefined}
          colorScheme="cyan"
          onClick={() => setShowUpload(true)}
          disabled={isResolved || isDraft}
        >
          Upload Documents
        </Button>
        <Button
          dataCy={dataCy ? `${dataCy}-request-reupload` : undefined}
          variant="outline"
          colorScheme="cyan"
          onClick={() => setShowReupload(true)}
          disabled={isResolved || isDraft}
        >
          Request re-upload
        </Button>
        {/* Deliberately enabled on a draft, unlike the two actions above. A
            processor hold can turn out to be spurious (already cleared with the
            rail, a duplicate event), and without this the only way to close the
            request was to invent a document ask and email the business for it.
            The server treats Draft -> Resolved as a close, not a promotion, so
            no notification goes out. */}
        <Button
          variant="outline"
          dataCy={dataCy ? `${dataCy}-mark-resolved` : undefined}
          colorScheme="cyan"
          onClick={() => setShowResolve(true)}
          disabled={isResolved}
        >
          Mark resolved
        </Button>
      </PermissionsProvider>

      {showResolve && (
        <ConfirmationModal
          dataCy={dataCy ? `${dataCy}-resolve-rfi` : undefined}
          isOpen={showResolve}
          title="Resolve RFI?"
          label={
            isDraft
              ? 'This closes the request without asking the business for anything. Use it when the hold turned out to be spurious. Continue?'
              : 'This marks the RFI as resolved. Continue?'
          }
          confirmActionLabel="Resolve"
          cancelActionLabel="Cancel"
          confirmAction={resolve}
          cancelAction={() => setShowResolve(false)}
          loading={isResolving}
          loadingLabel="Resolving..."
        />
      )}

      {showReupload && (
        <RequestReuploadModal
          rfi={rfi}
          dataCy={dataCy}
          isOpen={showReupload}
          isLoading={isRequesting}
          onClose={() => setShowReupload(false)}
          onSubmit={requestReupload}
        />
      )}

      {showSpecify && (
        <SendBusinessRFIModal
          businessId={rfi.businessId}
          rfi={rfi}
          isOpen={showSpecify}
          onClose={() => setShowSpecify(false)}
        />
      )}

      {showUpload && (
        <UploadRFIDocModal
          dataCy={dataCy}
          rfi={rfi}
          isOpen={showUpload}
          isLoading={isUploading}
          onClose={() => setShowUpload(false)}
          onSubmit={uploadRFIDocs}
        />
      )}
    </div>
  );
};

export default BusinessRFIActions;
